import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, ArrowLeft, Truck } from "lucide-react";
import StripeBadge from "@/components/StripeBadge";

const resolveImg = (img) => !img ? "https://images.unsplash.com/photo-1581147036324-c1c89c2c8b5c?w=600&q=80" : img.startsWith("http") ? img : fileUrl(img);

export default function BookingCheckout() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get("/bookings/my-rentals").then((r) => {
      const b = r.data.find((x) => x.id === bookingId);
      if (!b) { navigate("/dashboard"); return; }
      setBooking(b);
    });
  }, [bookingId]);

  const pay = async () => {
    setPaying(true);
    try {
      const res = await api.post("/payments/checkout", { booking_id: bookingId, origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed to start");
      setPaying(false);
    }
  };

  if (!booking) return <div className="py-32 flex justify-center"><div className="w-10 h-10 border-4 border-[#FF5A00] border-t-transparent rounded-full animate-spin" /></div>;

  const paid = booking.payment_status === "paid";
  const canPay = booking.status === "awaiting_payment" && !paid;

  const StatusNote = () => {
    if (paid) return (
      <div data-testid="already-paid" className="mt-6 flex items-center gap-2 bg-green-50 text-green-800 rounded-xl p-4 font-medium">
        <CheckCircle2 className="w-5 h-5" /> Payment complete — your booking is confirmed!
      </div>
    );
    if (booking.status === "pending") return (
      <div data-testid="awaiting-owner" className="mt-6 bg-amber-50 text-amber-800 rounded-xl p-4 text-sm font-medium">
        Waiting for the owner to accept your request. You'll be able to pay once it's accepted.
      </div>
    );
    if (booking.status === "declined") return (
      <div className="mt-6 bg-red-50 text-red-700 rounded-xl p-4 text-sm font-medium">The owner declined this request.</div>
    );
    if (booking.status === "cancelled") return (
      <div className="mt-6 bg-gray-50 text-gray-600 rounded-xl p-4 text-sm font-medium">This request was cancelled.</div>
    );
    if (booking.status === "expired") return (
      <div data-testid="payment-expired" className="mt-6 bg-gray-50 text-gray-600 rounded-xl p-4 text-sm font-medium">
        The 24-hour payment window expired. Please send a new request to the owner.
      </div>
    );
    return (
      <button data-testid="pay-now-btn" onClick={pay} disabled={paying} className="mt-6 w-full flex items-center justify-center gap-2 bg-[#0A0A0A] text-white rounded-full py-3.5 font-medium hover:bg-[#FF5A00] transition-colors active:scale-95 disabled:opacity-60">
        <CreditCard className="w-5 h-5" /> {paying ? "Redirecting…" : `Pay €${booking.total_amount}`}
      </button>
    );
  };

  return (
    <div className="max-w-lg mx-auto px-5 sm:px-8 py-12">
      <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"><ArrowLeft className="w-4 h-4" /> Back to dashboard</button>
      <div className="bg-white border border-black/10 rounded-2xl overflow-hidden">
        <img src={resolveImg(booking.listing_image)} alt="" className="w-full h-48 object-cover" />
        <div className="p-6">
          <h1 className="font-display text-2xl font-bold tracking-tight">{booking.listing_title}</h1>
          <p className="text-muted-foreground text-sm mt-1">Rented from {booking.owner_name}</p>

          <div className="mt-6 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Dates</span><span className="font-medium">{booking.start_date} → {booking.end_date}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">€{booking.price_per_day} × {booking.days} days</span><span className="font-medium">€{booking.rental_amount ?? booking.total_amount}</span></div>
            {booking.buyer_fee > 0 && (
              <div className="flex justify-between"><span className="text-muted-foreground">Service fee</span><span className="font-medium">€{booking.buyer_fee}</span></div>
            )}
            {booking.deposit_amount > 0 && (
              <div className="flex justify-between"><span className="text-muted-foreground">Refundable deposit</span><span className="font-medium">€{booking.deposit_amount}</span></div>
            )}
            <div className="flex justify-between border-t border-black/10 pt-2 font-display text-xl font-bold"><span>Total</span><span data-testid="checkout-total">€{booking.total_amount}</span></div>
            {booking.deposit_amount > 0 && <p className="text-xs text-muted-foreground">The deposit is released back to your card when the owner confirms the item was returned.</p>}
          </div>

          <StatusNote />
          <div data-testid="delivery-notice-checkout" className="mt-4 flex items-start gap-2.5 bg-[#FFF0E8] border border-[#FF5A00]/20 rounded-xl p-3.5">
            <Truck className="w-4 h-4 text-[#FF5A00] mt-0.5 shrink-0" />
            <p className="text-xs leading-relaxed text-[#0A0A0A]">
              <span className="font-semibold">Delivery &amp; pickup are arranged directly with {booking.owner_name}.</span>{" "}
              SelLend doesn't handle shipping — message the owner to agree on postage, collection, or drop-off and timing.
            </p>
          </div>
          {canPay && (
            <div className="mt-4 flex flex-col items-center gap-1.5">
              <StripeBadge className="mt-1" />
              <p className="text-xs text-muted-foreground text-center">Your payment & deposit are processed securely by Stripe. We never see your card details.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
